import {
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DatePipe, NgClass } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CertificateUploadService } from '../../../services/zertifikat-upload.service';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { IZertifikat } from '../../../shared/model/zertifikat.model';
import { Berater } from '../../../shared/model/berater.model';
import { BeraterService } from '../../../services/berater.service';
import {
  MatButton,
  MatIconButton,
  MatMiniFabButton,
} from '@angular/material/button';
import {
  MatError,
  MatFormField,
  MatInput,
  MatLabel,
  MatSuffix,
} from '@angular/material/input';
import {
  MatDatepicker,
  MatDatepickerActions,
  MatDatepickerApply,
  MatDatepickerCancel,
  MatDatepickerInput,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import { MatOption } from '@angular/material/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { TranslatePipe } from '@ngx-translate/core';
import { MessageService } from '../../../shared/components/message.service';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { DateService } from '../../../services/date.service';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { ModalService } from '../../../shared/components/modal/modal.service';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { RequestsService } from '../../../services/requests.service';
import {
  Requests,
  RequestsFactory,
  RequestType,
} from '../../../shared/model/request.model';
import { UserRoleService } from '../../../services/userRole.service';
import { INPUT_LIMITS } from '../../../shared/constants/input-limits';
import { FieldWarningComponent } from '../../../shared/components/field-warning/field-warning.component';
import {
  ValidationFinding,
  findEntryFinding,
} from '../../../shared/model/validation-finding';

@Component({
  selector: 'app-certificates',
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    NgClass,
    MatButton,
    MatLabel,
    MatFormField,
    MatInput,
    MatDatepickerInput,
    MatError,
    MatDatepickerToggle,
    MatDatepicker,
    MatDatepickerActions,
    MatDatepickerCancel,
    MatDatepickerApply,
    MatCheckbox,
    MatSuffix,
    TranslatePipe,
    MatCard,
    MatIcon,
    MatMiniFabButton,
    MatCardContent,
    NgClass,
    ModalComponent,
    MatOption,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatIconButton,
    FormsModule,
    FieldWarningComponent,
  ],
  templateUrl: './certificates.component.html',
  styleUrl: './certificates.component.scss',
})
export class CertificatesComponent implements OnInit, OnChanges {
  @Input() certificates: IZertifikat[] = [];
  @Input() exportMode: boolean;
  @Input() twoPagerMode: boolean = false;
  @Input() berater: Berater;
  @Input() forcedSelectedIds: string[] | null = null;
  @Input() findings: ValidationFinding[] = [];

  @Output() changedCertificates = new EventEmitter<Berater>();
  @Output() exportSelectionChange = new EventEmitter<string[]>();

  selectedExportIds: string[] = [];
  filteredCertificates: Array<string[]> = [];
  editMode: boolean = false;
  /** NP20-822: Laeuft gerade ein Speichern, loest ein weiterer Klick keinen zweiten Request aus. */
  isSaving = false;
  isCancelDisabled: boolean = false;
  form!: FormGroup;
  certificatesCopy: IZertifikat[];
  certificateNames: string[];
  originalCertificates: IZertifikat[];
  formChanged: boolean = false;
  showModal = false;
  showModalAttachment = false;
  currentIdx: number | null = null;
  allCertificatesAmount: number = 0;
  showModalAddRequest: boolean;
  addRequestFormInvalid: boolean = true;
  newReq: Requests;

  protected readonly environment = environment;
  protected get LIMITS() {
    return INPUT_LIMITS;
  }

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private certificateUploadService: CertificateUploadService,
    private beraterService: BeraterService,
    private messageService: MessageService,
    private dateService: DateService,
    @Inject(LOCALE_ID) public locale: string,
    private modalService: ModalService,
    private requestsService: RequestsService,
    private userService: UserRoleService,
  ) {}

  get certificatesFormArray(): FormArray {
    return this.form.get('zertifikate') as FormArray;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.form) return;
    if (changes['certificates'] && this.certificates) {
      this.certificatesFormArray.clear();
      this.certificates.forEach((z) => this.addCertificateForm(z));
      this.certificatesCopy = JSON.parse(JSON.stringify(this.certificates));
    }
    if (changes['exportMode'] || changes['twoPagerMode']) {
      if (this.exportMode && !this.twoPagerMode) {
        this.autoSelectAll();
      } else {
        this.selectedExportIds = [];
      }
    } else if (
      changes['certificates'] &&
      this.exportMode &&
      !this.twoPagerMode
    ) {
      this.autoSelectAll();
    }

    if (changes['forcedSelectedIds'] && this.forcedSelectedIds !== null) {
      this.selectedExportIds = [...this.forcedSelectedIds];
      this.exportSelectionChange.emit([...this.selectedExportIds]);
    }
  }

  private autoSelectAll(): void {
    this.selectedExportIds = (this.certificates ?? [])
      .map((c) => c.uuid)
      .filter((uuid): uuid is string => !!uuid);
    this.exportSelectionChange.emit([...this.selectedExportIds]);
  }

  private readonly MAX_CERTS_TWOPAGER = 6;

  canSelectCertificate(id: string): boolean {
    if (!this.twoPagerMode) return true;
    if (this.selectedExportIds.includes(id)) return true;
    return this.selectedExportIds.length < this.MAX_CERTS_TWOPAGER;
  }

  toggleExportSelection(idx: string, checked: boolean): void {
    if (checked) {
      if (
        this.twoPagerMode &&
        this.selectedExportIds.length >= this.MAX_CERTS_TWOPAGER
      )
        return;
      if (!this.selectedExportIds.includes(idx))
        this.selectedExportIds.push(idx);
    } else {
      this.selectedExportIds = this.selectedExportIds.filter((i) => i !== idx);
    }
    this.exportSelectionChange.emit([...this.selectedExportIds]);
  }

  ngOnInit() {
    this.form = this.fb.group({
      zertifikate: this.fb.array([]),
    });
    this.requestsService
      .getRequests(0, 1, RequestType.CERTIFICATE)
      .subscribe((value) => {
        this.allCertificatesAmount = value.page.totalElements;
        this.getAllCertificateNames(true);
      });
  }

  getAllCertificateNames(init?: boolean) {
    this.requestsService
      .getRequests(0, this.allCertificatesAmount, RequestType.CERTIFICATE, true)
      .subscribe((value) => {
        // Der Katalog kann denselben Namen mehrfach fuehren (unterschiedliche
        // Antraege, gleicher Lehrgang). Die Auswahlliste zeigt jeden Namen deshalb
        // nur einmal — sonst steht er mehrfach im Vorschlagsfeld und `track name`
        // in der Vorlage bekommt doppelte Schluessel (NG0955).
        this.certificateNames = [
          ...new Set(value.content.map((item) => item.name)),
        ];
        if (this.certificates?.length > 0 && init) {
          this.certificatesFormArray.clear();
          this.certificates.forEach((z) => this.addCertificateForm(z));
        }
        this.filteredCertificates = this.certificates.map(() => [
          ...this.certificateNames,
        ]);
      });
  }

  addCertificateForm(data?: IZertifikat) {
    const group = this.fb.group({
      uuid: [data?.uuid ?? null],
      beginn: [data?.beginn ?? '', Validators.required],
      schulungsanbieter: [
        data?.schulungsanbieter ?? '',
        [Validators.required, Validators.maxLength(INPUT_LIMITS.SHORT_LABEL)],
      ],
      lehrgang: [
        data?.lehrgang ?? '',
        [Validators.required, Validators.maxLength(INPUT_LIMITS.SHORT_LABEL)],
      ],
      forExport: [data?.forExport ?? false],
      trainingsanbieter: [
        data?.trainingsanbieter ?? '',
        [Validators.required, Validators.maxLength(INPUT_LIMITS.SHORT_LABEL)],
      ],
      zertifikatnummer: [
        data?.zertifikatnummer ?? '',
        [Validators.required, Validators.maxLength(INPUT_LIMITS.SHORT_LABEL)],
      ],
      uploadId: [data?.uploadId ?? null, Validators.required],
    });
    this.certificatesFormArray.push(group);
    this.filteredCertificates.push(
      this.certificateNames ? [...this.certificateNames] : [],
    );
  }

  newCertificate() {
    this.addCertificateForm();
  }

  // NP20-916: Loeschen nur vormerken. Vorher lief ein PUT mit der unveraenderten Liste — es meldete
  // Erfolg, ohne die Loeschung zu schicken, und sperrte zugleich das Abbrechen.
  delete(index: number) {
    this.certificatesFormArray.removeAt(index);
    this.formChanged = this.hasChanges();
    this.messageService.info('certificates.infoDeleteMessage');
  }

  save() {
    if (this.form.invalid || this.isSaving) return;
    this.certificates = this.certificatesFormArray.getRawValue();
    this.certificates = this.certificates.map((item) => ({
      ...item,
      beginn: this.dateService.toLocalDateString(item.beginn),
    }));
    // NP20-915: editMode erst nach der Antwort schliessen, sonst sieht ein Fehlschlag wie Erfolg aus.
    this.isSaving = true;
    this.beraterService
      .updateZertifikate(this.certificates, this.berater.id)
      .subscribe({
        next: (res) => {
          this.isSaving = false;
          this.editMode = false;
          this.berater = res;
          this.changedCertificates.emit(res);
          this.isCancelDisabled = false;
          this.certificates = this.berater.zertifikate;
          this.certificatesCopy = JSON.parse(JSON.stringify(this.certificates));
          this.messageService.success('certificates.successUpdateMessage');
        },
        error: () => {
          this.isSaving = false;
          // Bearbeitungsmodus bleibt offen, die Eingaben stehen weiter im Formular.
          this.messageService.error('certificates.errorUpdateMessage');
        },
      });
  }

  addAttachment(event: Event, index: number) {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];

      this.certificateUploadService.post(base64).subscribe({
        next: (uploadId) => {
          const group = this.certificatesFormArray.at(index) as FormGroup;
          if (this.hasChanges()) {
            this.isCancelDisabled = true;
          }
          group.patchValue({ uploadId });
          this.messageService.success('certificates.successMessage');
        },
        error: () => this.messageService.error('certificates.errorMessage'),
      });
    };
    reader.readAsDataURL(file);
  }

  deleteAttachment(index: number) {
    const group = this.certificatesFormArray.at(index) as FormGroup;
    const uploadId = group.get('uploadId')?.value;
    if (this.hasChanges()) {
      this.isCancelDisabled = true;
    }
    if (!uploadId) return;

    this.certificateUploadService.delete(uploadId).subscribe({
      next: () => {
        group.patchValue({ uploadId: null });
        this.messageService.success('certificates.successUploadDeleteMessage');
      },
      error: () => {
        this.messageService.error('certificates.errorCertificateUpload');
      },
    });
  }

  filterCertificates(value: string, index: number) {
    this.filteredCertificates[index] = this.certificateNames.filter(
      (certificate) => certificate.toLowerCase().includes(value.toLowerCase()),
    );
  }

  optionSelected(event: any, index: number) {
    this.certificatesFormArray
      .at(index)
      .get('lehrgang')!
      .setValue(event.option.value);
  }

  validateCourseSelection(index: number) {
    const control = this.certificatesFormArray.at(index).get('lehrgang');
    const value = control!.value;
    if (!this.certificateNames.includes(value)) {
      control!.setValue('');
    }
  }

  async downloadCertificates(exportId: string) {
    const url = `${this.environment.apiConfig.uri}api/zertifikate/${exportId}`;

    try {
      const localToken = this.userService.getJWT();
      const headers = new HttpHeaders({
        Authorization: `Bearer ${localToken}`,
      });

      const blob = await firstValueFrom(
        this.http.get(url, { headers, responseType: 'blob' }),
      );
      const downloadURL = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadURL;
      a.download = `Zertifikat-${exportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadURL);
    } catch (err) {
      console.error('Fehler beim Herunterladen des Zertifikats:', err);
    }
  }

  onToggleEdit(): void {
    if (this.editMode) {
      this.save();
    } else {
      if (this.certificates.length === 0) {
        this.addCertificateForm();
      } else {
        this.certificatesFormArray.clear();
        this.certificates.forEach((z) => this.addCertificateForm(z));
      }
      this.originalCertificates = JSON.parse(JSON.stringify(this.certificates));
      this.certificatesCopy = JSON.parse(
        JSON.stringify(this.form.value.zertifikate),
      );
      this.editMode = true;
      this.formChanged = false;
    }
  }

  onCancelEdit(): void {
    this.certificates = JSON.parse(JSON.stringify(this.originalCertificates));

    this.certificatesFormArray.clear();
    this.certificates.forEach((z) => this.addCertificateForm(z));

    this.editMode = false;
    this.formChanged = false;
  }

  hasChanges(): boolean {
    return (
      JSON.stringify(this.form.value.zertifikate) !==
      JSON.stringify(this.certificatesCopy)
    );
  }

  openDeleteModal(idx: number) {
    this.currentIdx = idx;
    this.modalService.open('deleteCertificate');
  }

  isValid() {
    return this.certificatesFormArray.valid;
  }

  openDeleteAttachmentModal(idx: number) {
    this.currentIdx = idx;
    this.showModalAttachment = true;
  }

  addCertificateRequest() {
    this.requestsService
      .newRequest(RequestType.CERTIFICATE, this.newReq)
      .subscribe({
        next: () =>
          this.messageService.success('request.modals.successEditing'),
        error: () => this.messageService.error('request.modals.errorEditing'),
      });
  }

  onAddRequestFormChange() {
    this.addRequestFormInvalid = !this.newReq?.name?.trim();
  }

  onAddRequestClick() {
    this.showModalAddRequest = true;
    this.newReq = RequestsFactory.empty();
  }

  entryFinding(entryIndex: number, field: string): ValidationFinding | null {
    if (this.editMode || this.exportMode) return null;
    return findEntryFinding(this.findings, entryIndex, field);
  }
}
